import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import Hub from "./pages/Hub";
import CreateUser from "./pages/CreateUser";
import Training from "./pages/Training";
import ProsodyTraining from "./pages/ProsodyTraining";
import PhraseTraining from "./pages/PhraseTraining";
import WebDemo from "./pages/WebDemo";
import Header from "./components/Header";
import "./styles/global.css";
import axios from 'axios';

// Constants for token refresh
const TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes
const ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity before stopping refresh

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("jwt"));
    const [userRole, setUserRole] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const hasTrainingAccess = ["training-user", "multi-user", "admin"].includes(userRole);

    // Track user activity
    useEffect(() => {
        const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        
        const updateActivity = () => {
            setLastActivity(Date.now());
        };

        activityEvents.forEach(event => {
            window.addEventListener(event, updateActivity);
        });

        return () => {
            activityEvents.forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
        };
    }, []);

    const handleLogout = useCallback((message = "") => {
        localStorage.removeItem("jwt");
        setIsLoggedIn(false);
        setUserRole(null);
        if (message) {
            setSessionExpired(true);
            localStorage.setItem("loginMessage", message);
        }
    }, []);

    const checkTokenExpiration = useCallback(() => {
        const token = localStorage.getItem("jwt");
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split(".")[1]));
                const expirationTime = payload.exp * 1000; // Convert to milliseconds
                
                if (Date.now() >= expirationTime) {
                    handleLogout("Your session has expired. Please log in again.");
                    return false;
                }
                return true;
            } catch (error) {
                handleLogout("Invalid session. Please log in again.");
                return false;
            }
        }
        return false;
    }, [handleLogout]);

    const refreshUserState = useCallback(() => {
        const token = localStorage.getItem("jwt");
        if (token && checkTokenExpiration()) {
            const user = JSON.parse(atob(token.split(".")[1]));
            setUserRole(user.role);
            setIsLoggedIn(true);
        } else {
            setUserRole(null);
            setIsLoggedIn(false);
        }
    }, [checkTokenExpiration]);

    // Token refresh logic
    const refreshToken = useCallback(async () => {
        try {
            const response = await axios.post(
                'https://asldemologinauth-a8b2fpbnhrgygsaj.canadacentral-01.azurewebsites.net/auth/refresh',
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('jwt')}`
                    }
                }
            );

            if (response.data.success) {
                localStorage.setItem('jwt', response.data.token);
                refreshUserState();
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to refresh token:', error);
            if (error.response?.status === 401) {
                const shouldAutoLogout = process.env.REACT_APP_AUTO_LOGOUT_ON_UNAUTHORIZED !== 'false';
                if (shouldAutoLogout) {
                    handleLogout("Your session has expired. Please log in again.");
                } else {
                    console.warn('Token refresh failed with 401, but auto-logout is disabled in development');
                }
            }
            return false;
        }
    }, [handleLogout, refreshUserState]);

    // Periodic token refresh
    useEffect(() => {
        if (!isLoggedIn) return;

        const refreshInterval = setInterval(async () => {
            const currentTime = Date.now();
            const inactiveTime = currentTime - lastActivity;

            // Only refresh if user has been active within ACTIVITY_TIMEOUT
            if (inactiveTime < ACTIVITY_TIMEOUT) {
                await refreshToken();
            }
        }, TOKEN_REFRESH_INTERVAL);

        return () => clearInterval(refreshInterval);
    }, [isLoggedIn, lastActivity, refreshToken]);

    // Check token expiration periodically
    useEffect(() => {
        refreshUserState();
        const interval = setInterval(checkTokenExpiration, 60000); // Check every minute
        return () => clearInterval(interval);
    }, [checkTokenExpiration, refreshUserState]);

    // Set up axios interceptor for 401 errors
    useEffect(() => {
        const setupAxiosInterceptor = async () => {
            const axios = (await import('axios')).default;
            axios.interceptors.response.use(
                response => response,
                error => {
                    if (error.response && error.response.status === 401) {
                        // Only auto-logout if enabled in environment (defaults to true for production)
                        const shouldAutoLogout = process.env.REACT_APP_AUTO_LOGOUT_ON_UNAUTHORIZED !== 'false';
                        if (shouldAutoLogout) {
                            handleLogout("Your session has expired. Please log in again.");
                        } else {
                            console.warn('Unauthorized error received, but auto-logout is disabled in development');
                        }
                    }
                    return Promise.reject(error);
                }
            );
        };
        setupAxiosInterceptor();
    }, [handleLogout]);

    return (
        <Router>
            <div className="app-container">
                <Routes>
                    {/* public routes */}
                    <Route 
                        path="/login" 
                        element={
                            <Login 
                                refreshUserState={refreshUserState}
                                sessionExpired={sessionExpired}
                                setSessionExpired={setSessionExpired}
                            />
                        } 
                    />

                    {/* private routes */}
                    {isLoggedIn ? (
                        <>
                            <Route path="/" element={<Hub userRole={userRole} />} />
                            
                            {/* private pages with Header */}
                            <Route
                                path="/training"
                                element={
                                    hasTrainingAccess ? (
                                        <>
                                            <Header />
                                            <Training />
                                        </>
                                    ) : (
                                        <Navigate to="/" replace />
                                    )
                                }
                            />
                            <Route
                                path="/phrase-training"
                                element={
                                    hasTrainingAccess ? (
                                        <>
                                            <Header />
                                            <PhraseTraining />
                                        </>
                                    ) : (
                                        <Navigate to="/" replace />
                                    )
                                }
                            />
                            <Route
                                path="/batch-training"
                                element={
                                    hasTrainingAccess ? (
                                        <>
                                            <Header />
                                            <ProsodyTraining />
                                        </>
                                    ) : (
                                        <Navigate to="/" replace />
                                    )
                                }
                            />
                            <Route
                                path="/web-demo"
                                element={
                                    userRole !== "training-user" ? (
                                        <>
                                            <Header />
                                            <WebDemo />
                                        </>
                                    ) : (
                                        <Navigate to="/" replace />
                                    )
                                }
                            />
                            <Route
                                path="/create-user"
                                element={
                                    userRole === "admin" ? (
                                        <>
                                            <Header />
                                            <CreateUser />
                                        </>
                                    ) : (
                                        <Navigate to="/" replace />
                                    )
                                }
                            />
                            
                            {/* fallback for any other routes */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </>
                    ) : (
                        <>
                            <Route path="/login" element={<Login refreshUserState={refreshUserState} />} />
                            <Route path="*" element={<Navigate to="/login" replace />} />
                        </>
                    )}
                </Routes>
            </div>
        </Router>
    );
}

export default App;
